import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme, neutral, garnetRed, jadeGreen, lightBlue } from '../../design-system/theme';
import {
  CardHeader,
  EmptyStateContainer,
  EmptyStateText,
} from '../../styles/adminShared';
import { adminApi } from '../../services/adminApi';

const BellContainer = styled.div`
  position: relative;
`;

const BellButton = styled.button<{ $hasUnread: boolean }>`
  position: relative;
  padding: 10px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease;

  &:hover {
    background: var(--ds-surface-muted, #f5f7fa);
  }

  svg {
    width: 20px;
    height: 20px;
    color: var(--ds-text-secondary, #565a74);
  }

  ${props => props.$hasUnread && `
    &::after {
      content: '';
      position: absolute;
      top: 8px;
      right: 8px;
      width: 8px;
      height: 8px;
      background: ${garnetRed[5]};
      border-radius: 50%;
      border: 2px solid white;
    }
  `}
`;

// Notification-specific styled component (counter badge)
const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  background: ${garnetRed[5]};
  color: white;
  font-size: 10px;
  font-weight: 600;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
`;

// Notification-specific dropdown panel styling
const Dropdown = styled.div<{ $open: boolean }>`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 360px;
  max-height: 480px;
  opacity: ${props => props.$open ? 1 : 0};
  visibility: ${props => props.$open ? 'visible' : 'hidden'};
  transform: ${props => props.$open ? 'translateY(0)' : 'translateY(-10px)'};
  transition: all 0.2s ease;
  z-index: 1000;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

// Wrapper for dropdown header to apply custom positioning styles
const DropdownHeaderWrapper = styled(CardHeader)`
  padding: 16px 20px;
`;

const DropdownTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--ds-text-primary, #212435);
  margin: 0;
`;

const MarkAllButton = styled.button`
  font-size: 13px;
  color: var(--ds-primary, #363a50);
  background: transparent;
  border: none;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const NotificationList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

// Notification item-specific styling
const NotificationItem = styled.div<{ $unread: boolean }>`
  display: flex;
  gap: 12px;
  padding: 14px 20px;
  background: ${props => props.$unread ? `rgba(${parseInt(garnetRed[5].slice(1, 3), 16)}, ${parseInt(garnetRed[5].slice(3, 5), 16)}, ${parseInt(garnetRed[5].slice(5, 7), 16)}, 0.05)` : 'transparent'};
  border-bottom: 1px solid var(--ds-border, #e2e8f0);
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover {
    background: var(--ds-surface-muted, #f5f7fa);
  }

  &:last-child {
    border-bottom: none;
  }
`;

// Notification type-specific icon styling
const NotificationIcon = styled.div<{ $type: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  ${props => {
    switch (props.$type) {
      case 'consent':
        return `background: ${jadeGreen[1]}; color: ${jadeGreen[6]};`;
      case 'withdrawal':
        return `background: ${garnetRed[1]}; color: ${garnetRed[5]};`;
      case 'user':
        return `background: ${lightBlue[1]}; color: ${lightBlue[6]};`;
      case 'system':
        return `background: ${neutral[2]}; color: ${neutral[6]};`;
      default:
        return `background: ${neutral[2]}; color: ${neutral[6]};`;
    }
  }}

  svg {
    width: 18px;
    height: 18px;
  }
`;

const NotificationContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const NotificationTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--ds-text-primary, #212435);
  margin-bottom: 4px;
  
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const NotificationMessage = styled.div`
  font-size: 13px;
  color: var(--ds-text-secondary, #565a74);
  line-height: 1.4;
  
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const NotificationTime = styled.div`
  font-size: 11px;
  color: var(--ds-text-subtle, #8b8fa9);
  margin-top: 6px;
`;

// Notification-specific unread indicator
const UnreadDot = styled.div`
  width: 8px;
  height: 8px;
  background: ${theme.accent};
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 6px;
`;

// Wrapper for empty state with custom padding for dropdown context
const EmptyStateWrapper = styled(EmptyStateContainer)`
  padding: 40px 20px;

  svg {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.5;
  }
`;

const DropdownFooter = styled.div`
  padding: 12px 20px;
  border-top: 1px solid var(--ds-border, #e2e8f0);
  text-align: center;
`;

const ViewAllLink = styled.a`
  font-size: 13px;
  color: var(--ds-primary, #363a50);
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

// Notification data type matching backend API response
export interface Notification {
  id: string;
  type: 'consent' | 'withdrawal' | 'user' | 'system';
  title: string;
  message: string;
  timestamp: Date;
  read: boolean;
  link?: string;
}

// Backend API notification shape
interface ApiNotification {
  id: string;
  type: string;
  title: string;
  message: string;
  createdAt: string;
  read: boolean;
  link?: string;
}

const POLL_INTERVAL_MS = 60_000; // 60 seconds

/**
 * Notification bell icon with dropdown showing recent notifications.
 * Self-manages data by fetching from the real backend API.
 * Polls unread count every 60 seconds and fetches list on dropdown open.
 */
export const NotificationBell: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Map API response to local Notification type
  const mapApiNotification = (n: ApiNotification): Notification => ({
    id: n.id,
    type: (n.type?.toLowerCase() as Notification['type']) || 'system',
    title: n.title,
    message: n.message,
    timestamp: new Date(n.createdAt),
    read: n.read,
    link: n.link,
  });

  // Fetch unread count
  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await adminApi.getNotificationUnreadCount();
      setUnreadCount(data.count ?? data.unreadCount ?? 0);
    } catch (err) {
      // Silently ignore - notifications are non-critical
      console.debug('Failed to fetch notification unread count', err);
    }
  }, []);

  // Fetch notifications list
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await adminApi.getNotifications({ unreadOnly: false, page: 0, pageSize: 20 });
      const items: ApiNotification[] = data.data || data.notifications || data.content || [];
      setNotifications(items.map(mapApiNotification));
    } catch (err) {
      console.debug('Failed to fetch notifications', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll unread count on mount + every 60s
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch list when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark a single notification as read
  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      try {
        await adminApi.markNotificationRead(notification.id);
        setNotifications(prev =>
          prev.map(n => n.id === notification.id ? { ...n, read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.debug('Failed to mark notification as read', err);
      }
    }
    setIsOpen(false);
  };

  // Mark all notifications as read
  const handleMarkAllRead = async () => {
    try {
      await adminApi.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.debug('Failed to mark all notifications as read', err);
    }
  };

  const formatTimeAgo = (date: Date) => {
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'consent':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'withdrawal':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        );
      case 'user':
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      default:
        return (
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  return (
    <BellContainer ref={containerRef}>
      <BellButton
        $hasUnread={unreadCount > 0}
        onClick={() => setIsOpen(!isOpen)}
        title={`${unreadCount} unread notifications`}
      >
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && <Badge>{unreadCount > 9 ? '9+' : unreadCount}</Badge>}
      </BellButton>

      <Dropdown $open={isOpen}>
        <DropdownHeaderWrapper>
          <DropdownTitle>Notifications</DropdownTitle>
          {unreadCount > 0 && (
            <MarkAllButton onClick={handleMarkAllRead}>
              Mark all read
            </MarkAllButton>
          )}
        </DropdownHeaderWrapper>

        <NotificationList>
          {loading ? (
            <EmptyStateWrapper>
              <EmptyStateText>Loading...</EmptyStateText>
            </EmptyStateWrapper>
          ) : notifications.length === 0 ? (
            <EmptyStateWrapper>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <EmptyStateText>No notifications yet</EmptyStateText>
            </EmptyStateWrapper>
          ) : (
            notifications.slice(0, 5).map(notification => (
              <NotificationItem
                key={notification.id}
                $unread={!notification.read}
                onClick={() => handleNotificationClick(notification)}
              >
                <NotificationIcon $type={notification.type}>
                  {getNotificationIcon(notification.type)}
                </NotificationIcon>
                <NotificationContent>
                  <NotificationTitle>{notification.title}</NotificationTitle>
                  <NotificationMessage>{notification.message}</NotificationMessage>
                  <NotificationTime>{formatTimeAgo(notification.timestamp)}</NotificationTime>
                </NotificationContent>
                {!notification.read && <UnreadDot />}
              </NotificationItem>
            ))
          )}
        </NotificationList>

        {notifications.length > 0 && (
          <DropdownFooter>
            <ViewAllLink href="/admin/notifications" onClick={() => setIsOpen(false)}>
              View all notifications
            </ViewAllLink>
          </DropdownFooter>
        )}
      </Dropdown>
    </BellContainer>
  );
};

export default NotificationBell;
